import { useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { ArrowLeft, Eye, EyeOff, Pencil, Pin, PinOff, SquarePen, Trash2, UserPlus } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { conversationDisplayTitle } from '../../../lib/conversation-display-title';
import type { Conversation } from '../../../lib/conversations';
import { useConversationActions, CONVERSATIONS_PAGE } from '../../../lib/use-conversation-actions';
import { setRecapVisibleFor } from '../../../lib/recap-preference';
import { useRecapVisible } from '../../../lib/use-recap-visible';
import { DeleteConversationConfirm } from '../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../conversations/RenameConversationDialog';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { AgentsIslandBadge } from '../AgentsIslandBadge';
import { Button, IconButton } from '../ui';
import { OverflowMenu, type OverflowMenuAction } from '../ui/OverflowMenu';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useValueFirstIsland } from '../../../lib/value-first-island';
import { useConversationMonitorLongPress } from './use-conversation-monitor';
import { headerStyle, newChatTitleStyle } from './styles';
import styles from './ChatHeader.module.css';
import { CloseIcon, ExpandIcon, FilesIcon, ShrinkIcon } from './icons';
import { viewerRole } from '../../../lib/group-chat';
import { useGroupSummary } from '../../../lib/use-group-summary';
import { addPeopleOffered } from '../../../lib/group-chat-actions';
import { GroupChatHeader, GroupParticipantsSheet } from '../group-chat/GroupChatHeader';
import { WaGroupChatHeader } from '../settings-whatsapp/WaGroupChatHeader';
import { AddPeopleDialog } from '../group-chat/GroupChatDialogs';

export interface ChatHeaderProps {
  chatsScreen?: boolean;
  onBackToChats?: () => void;
  narrow: boolean;
  fullscreen: boolean;
  isNewChat: boolean;
  headerTitle: string | undefined;
  /** A badge drawn after the title — the "Meeting" pill over a meeting's write-up. Wide only, like the title. */
  badge?: ReactNode;
  /** A quiet second line under the title — the meeting's own time and duration. Wide only, like the title. */
  subtitle?: string;
  onNewConversation: () => void;
  onCompose: () => void;
  onExpand: () => void;
  onShrink: () => void;
  onMinimize: () => void;
  // Opens the per-chat Files/Media panel. Undefined => no listable conversation
  // yet (a brand-new, never-sent chat), so the button is hidden.
  onOpenFiles?: () => void;
  /** The conversation this header is for. Undefined on a brand-new chat, which has no row to act on yet —
   *  so the pin/rename/delete items are simply absent rather than present and inert. */
  conversation?: Conversation;
}

// Under the title, in the secondary ink — the meeting's own span, the same type the agent page's
// history row sets its time in.
const headerSubtitleStyle: CSSProperties = {
  padding: '0 6px',
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 'var(--sym-weight-medium)',
  letterSpacing: '-0.02em',
  color: 'var(--ds-text-secondary)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export function ChatHeader({
  chatsScreen,
  onBackToChats,
  narrow,
  fullscreen,
  isNewChat,
  headerTitle,
  badge,
  subtitle,
  onNewConversation,
  onCompose,
  onExpand,
  onShrink,
  onMinimize,
  onOpenFiles,
  conversation,
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  const recapVisible = useRecapVisible(conversation?.id);
  // The overflow menu hangs from the header's bottom edge (Figma), not the trigger's — so it clears the
  // header by the same amount whether the trigger is the 30px desktop button or the 36px mobile circle.
  const headerRef = useRef<HTMLElement>(null);
  // Dev-only, VPN/dev-build gated: HOLD expand/shrink to copy this thread's Symphony Live URL. Inert —
  // and request-free — for a real user, and inert on a brand-new chat with no conversation to monitor.
  const monitor = useConversationMonitorLongPress(conversation?.id);
  // Dev-only English, like the rest of the dev panel: nothing a real user ever sees, so no catalog entry.
  const monitorNotice = monitor.notice && (
    <div className={styles.monitorNotice} role="status">
      <span>{monitor.notice.copied ? 'Monitor URL copied' : 'Copy failed'}</span>
      <Button variant="contained" size="tiny" color="light" onClick={() => monitor.openMonitor(monitor.notice!.url)}>
        Open monitor
      </Button>
    </div>
  );
  // A Symphony GROUP chat (behind `symphonyGroupChat`) swaps the plain title for the group header: avatars, name,
  // member count, and the participant list on tap. A private chat keeps today's header exactly.
  const { enabled: groupChatEnabled, summary: group, viewerState: groupViewerState } = useGroupSummary(conversation?.id, conversation);
  // Phase 3b: "Add people" — the owner on a private chat too (the first invite makes it a group), and the owner and
  // admins on a group. A group's owner deleting the chat ends the group instead (design §3.5) — never a silent delete.
  // A WhatsApp-group thread (not a Symphony group): its own header — name, roster subtitle, and the tap to its screen.
  const waGroupThreadId = !group && conversation?.isWaGroup && conversation.id ? conversation.id : null;
  const groupRole = group ? viewerRole(group) : null;
  const canAddPeople = addPeopleOffered(groupChatEnabled, conversation?.id, group, conversation?.viewerState !== undefined);
  const [addPeopleOpen, setAddPeopleOpen] = useState(false);
  const [endGroupOpen, setEndGroupOpen] = useState(false);

  const newChat = () => {
    logPageClickBi({ page_name: 'chat', button_name: 'new_chat' });
    (narrow && !chatsScreen ? onCompose : onNewConversation)();
  };
  // Whether the standalone New-Chat affordance shows (the compose icon on mobile and in the dock, the labeled
  // button when expanded — the /chats split column then drops it, see below): not already sitting on an
  // empty new chat (unless this is the /chats list on mobile). Never while a value-first setup runs: a new chat
  // would leave the onboarding with no way back to it.
  const valueFirstSetup = !!useValueFirstIsland()?.expanded;
  const newChatShown = (!isNewChat || (chatsScreen && narrow)) && !valueFirstSetup;

  const menuActions: OverflowMenuAction[] = [];
  if (onOpenFiles) {
    menuActions.push({
      key: 'chat-files',
      label: t('chatFiles.title'),
      icon: <FilesIcon />,
      onSelect: () => {
        logPageClickBi({ page_name: 'chat', button_name: 'open_chat_files' });
        onOpenFiles();
      },
    });
  }
  // The same three actions the conversation's row in the sidebar offers, for the conversation you are
  // actually IN — reached here because that is where a user thinks about the chat they are reading, and
  // because the mobile header is the only chrome on that screen. The labels, order and destructive skin
  // match the row menu exactly; the writes are the same shared flow, so the two cannot diverge.
  // Guard on the ID, not the conversation: a row with no server id cannot be pinned, renamed or deleted —
  // every one of those writes addresses it by id — and guarding on the id is also what narrows it to a string.
  const convId = conversation?.id;
  if (convId && canAddPeople) {
    menuActions.push({
      key: 'add-people',
      label: t('chat.group.addPeople'),
      icon: <UserPlus size={16} aria-hidden />,
      onSelect: () => {
        logPageClickBi({ page_name: 'chat', button_name: 'group_add_people' });
        setAddPeopleOpen(true);
      },
    });
  }
  // A JOINED group is not the viewer's conversation: no pin/rename/delete of the owner's row from here (the Chats
  // row menu carries the member's own pin / mute / archive).
  if (convId && !groupViewerState) {
    // The SAME name→start-time→"Untitled" rule the chats list and the rail apply, so the delete
    // confirm and the rename draft call a thread what the row that opened them calls it.
    // `t` is typed as possibly-undefined and the dialogs' target takes a real string, hence the `|| ''`.
    const title: string = conversationDisplayTitle(
      conversation?.name, conversation?.createdDate, t('common.untitled') || '',
    );
    const pinned = Boolean(conversation?.pinned);
    menuActions.push(
      {
        key: 'pin',
        label: pinned ? t('conversations.unpin') : t('conversations.pin'),
        icon: pinned ? <PinOff size={16} aria-hidden /> : <Pin size={16} aria-hidden />,
        onSelect: () => void flow.togglePin({ id: convId, pinned }),
      },
      {
        key: 'rename',
        label: t('conversations.rename.action'),
        icon: <Pencil size={16} aria-hidden />,
        onSelect: () => flow.requestRename({ id: convId, title }),
      },
      // The way back from the recap banner's ×, which hides the line on THIS conversation. It names the
      // outcome the way the pin item does, so the label is what pressing it will do.
      {
        key: 'recap',
        label: recapVisible ? t('chat.recap.hide') : t('chat.recap.show'),
        icon: recapVisible ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />,
        onSelect: () => {
          logPageClickBi({ page_name: 'chat', button_name: recapVisible ? 'disable_recap' : 'enable_recap' });
          setRecapVisibleFor(convId, !recapVisible);
        },
      },
      {
        key: 'delete',
        label: t('conversations.delete.action'),
        icon: <Trash2 size={16} aria-hidden />,
        destructive: true,
        onSelect: () => (group && groupRole === 'OWNER' ? setEndGroupOpen(true) : flow.requestDelete({ id: convId, title })),
      },
    );
  }

  return (
    <>
    <header
      ref={headerRef}
      style={{
        ...headerStyle,
        // The centred island below is absolute; without this it would resolve against the fixed chat surface
        // and centre on the whole panel rather than on this row.
        position: 'relative',
        ...(narrow || isNewChat ? { borderBottom: 'none' } : null),
        ...(narrow ? { justifyContent: 'flex-end' } : null),
      }}
    >
      {narrow ? null : group ? (
        <GroupChatHeader summary={group} fallbackTitle={headerTitle ?? ''} conversationId={conversation?.id} />
      ) : waGroupThreadId ? (
        <WaGroupChatHeader title={headerTitle ?? ''} conversationId={waGroupThreadId} waGroupId={conversation?.waGroupId} />
      ) : (
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={newChatTitleStyle}>{headerTitle}</span>
            {badge}
          </span>
          {subtitle && <span style={headerSubtitleStyle}>{subtitle}</span>}
        </span>
      )}

      {/* CENTRED, whenever the chat COVERS the screen — mirroring native's chat header, which centres the
          island and draws it before the overflow menu. That is the one case needing its own copy: the chat
          surface is a fixed z-50 layer, so AppFrame's absolute z-6 top-bar island is covered by it and cannot
          be seen at all. On a phone every open chat covers the
          screen — `fullscreen` is a wide-only mode — so narrow counts too. Docked chat keeps using the top-bar
          island; rendering here as well would put TWO islands on screen and register the chip slot twice.
          The /chats screen follows the SAME rule: its wide SPLIT column is a dock (the list's title row carries
          the island beside it, so none here), while a chat that covers the list — narrow, or grown to
          `fullscreen` by ⤢ — needs its own island like any other. Narrow /chats draws its back arrow AFTER
          the island, beside the actions: the hamburger holds the top-left corner, so the back button there
          would sit under it, and it is icon-only so the island still fits a 390px phone. */}
      {(narrow || fullscreen) && (
        // Wide has room to centre on the whole row. A phone does not: the BrandHeader hamburger occupies the
        // top-left, so the island takes the leftover space as a flex item and centres inside THAT, clearing
        // the 44px hamburger with paddingInlineStart.
        <div
          style={
            narrow
              ? { flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', paddingInlineStart: 44 }
              : { position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex' }
          }
        >
          <GenericEventsRouteProvider value="agents-island">
            <AgentsIslandBadge side="center" />
          </GenericEventsRouteProvider>
        </div>
      )}
      {chatsScreen && narrow && (
        <IconButton variant="text" onClick={onBackToChats} aria-label={t('chats.title')} title={t('chats.title')}>
          <ArrowLeft size={16} className="dir-flip" aria-hidden />
        </IconButton>
      )}
      {/* Keep New Chat visible without opening the secondary actions menu. It carries its LABEL only when
          the chat is EXPANDED — every other state is a narrow column that the label does not fit in:
          - narrow (phone / stacked): the labeled pill shares the flex row with the centered island's
            flex:1 container, so its width pushes the island off-center;
          - side dock and the /chats split column (both `!narrow && !fullscreen`, since `liveFullscreen` is
            forced off on the Chats screen): the header is a ~dock-width row, so the pill shrank onto two
            lines and read "New / Chat" (reported, with a screenshot).
          So the signal is the chat's own width, NOT the viewport's — a wide viewport says nothing about how
          much room this header has. Icon-only keeps its accessible name, so nothing is lost but the glyph.
          Exception to three-surface parity, written down: the side dock and the expanded chat are WEB-ONLY
          layout modes; iOS and Android always render the phone header, which is this component's `narrow`
          pill (`ChatHeader.swift` / `chat/Chat.kt`), so the wide variants have nothing to mirror. */}
      <div className={styles.actions}>
        {narrow ? (
          (newChatShown || menuActions.length > 0) && (
            <div className={styles.mobilePill}>
              {newChatShown && (
                <IconButton size="small" className={styles.pillIcon} onClick={newChat} aria-label={t('chats.newChat')} title={t('chats.newChat')}>
                  <SquarePen size={16} aria-hidden />
                </IconButton>
              )}
              {menuActions.length > 0 && (
                <OverflowMenu actions={menuActions} ariaLabel={t('chat.moreActions')} triggerClassName={styles.pillTrigger} blockAnchorRef={headerRef} blockAnchorGap={8} />
              )}
            </div>
          )
        ) : (
          <>
            {newChatShown && (
              fullscreen ? (
                // The SAME outlined, label-only button as the /chats title row (Figma 20312-114342 /
                // 20312-113774), so the list and the expanded chat show one New Chat.
                <Button variant="outlined" className={styles.newChat} onClick={newChat} aria-label={t('chats.newChat')}>
                  {t('chats.newChat')}
                </Button>
              ) : chatsScreen ? null : (
                // Docked beside the /chats list the header carries no New Chat — the list's title row
                // already has one right next to it (Figma 20312-113864: "⋮ ⤢ ×"). Docked over any other page
                // it is the compose glyph, the same one the mobile pill uses (Figma 20312-114317).
                <IconButton size="small" onClick={newChat} aria-label={t('chats.newChat')} title={t('chats.newChat')}>
                  <SquarePen size={16} aria-hidden />
                </IconButton>
              )
            )}
            {menuActions.length > 0 && (
              // Wide: the ⋮ hovers like the expand/close buttons beside it and wears a selected fill while
              // its menu is open (Figma "Chat Header" 14692:214629).
              <OverflowMenu actions={menuActions} ariaLabel={t('chat.moreActions')} triggerClassName={styles.triggerSelectable} blockAnchorRef={headerRef} />
            )}
          </>
        )}
        {/* ⤢ and × on EVERY wide chat: the /chats split column IS a dock, so it takes the dock's own chrome. */}
        {!narrow &&
          (fullscreen ? (
            <span className={styles.monitorAnchor}>
              <IconButton size="small" {...monitor.handlers} onClick={() => { if (monitor.consumeLongPress()) return; logPageClickBi({ page_name: 'chat', button_name: 'shrink_chat' }); onShrink(); }} aria-label={t('chat.float')} title={t('chat.floatShort')}>
                <ShrinkIcon />
              </IconButton>
              {monitorNotice}
            </span>
          ) : (
            <span className={styles.monitorAnchor}>
              <IconButton size="small" {...monitor.handlers} onClick={() => { if (monitor.consumeLongPress()) return; logPageClickBi({ page_name: 'chat', button_name: 'expand_chat' }); onExpand(); }} aria-label={t('chat.expand')} title={t('chat.expandShort')}>
                <ExpandIcon />
              </IconButton>
              {monitorNotice}
            </span>
          ))}
        {!narrow && (
          <IconButton size="small" onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'minimize_chat' }); onMinimize(); }} aria-label={t('chat.minimize')} title={t('chat.minimizeShort')}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    </header>
    {/* On a phone the header row is the island and the actions, with no title slot — so a group's header rides
        its own row under it, the same content as the wide title. */}
    {narrow && group && (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '0 16px 8px' }}>
        <GroupChatHeader summary={group} fallbackTitle={headerTitle ?? ''} conversationId={conversation?.id} />
      </div>
    )}
    {/* A WhatsApp group's header rides the same row on a phone: its name, who it is for, and the way to its screen. */}
    {narrow && !group && waGroupThreadId && (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '0 16px 8px' }}>
        <WaGroupChatHeader title={headerTitle ?? ''} conversationId={waGroupThreadId} waGroupId={conversation?.waGroupId} />
      </div>
    )}
    {addPeopleOpen && convId && <AddPeopleDialog conversationId={convId} summary={group} onClose={() => setAddPeopleOpen(false)} />}
    {/* The owner's Delete on a group opens the participant sheet's End group confirm. */}
    {endGroupOpen && group && convId && <GroupParticipantsSheet summary={group} conversationId={convId} initialEnd onClose={() => setEndGroupOpen(false)} />}
    <DeleteConversationConfirm flow={flow} />
    <RenameConversationDialog flow={flow} />
    </>
  );
}
